// src/pages/EditMessage.jsx
import React, { useState, useEffect } from "react";
import {
  FormRowSelect,
  FormRowSelectId,
  ListSelection,
  BirthdayMessageDetails,
  GeneralMessageDetails,
  Loading,
} from "../components";
import Wrapper from "../assets/wrappers/DashboardFormPage";
import {
  useOutletContext,
  useLoaderData,
  useNavigation,
  Form,
  redirect,
  useParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { customFetch } from "../utils/custom-fetch.js";

// Loader: מושך את ההודעה וכל הבניינים למילוי הרשימות
export async function loader({ params }) {
  try {
    const msgRes = await customFetch.get(`/messages/${params.id}`);
    const bldRes = await customFetch.get("/buildings");
    const buildingsArray = Array.isArray(bldRes.data.buildings)
      ? bldRes.data.buildings
      : [];

    return {
      message: msgRes.data,
      buildings: buildingsArray,
    };
  } catch (err) {
    toast.error(err.response?.data?.msg || "שגיאה בטעינת הנתונים");
    return redirect("/messages");
  }
}

// Action: שולח PUT לעדכון
export async function action({ request, params }) {
  const formData = await request.formData();
  const data = Object.fromEntries(formData);
  
  // אריזה של fields חוזרים מרובים
  const cities = formData.getAll("cities");
  const streets = formData.getAll("streets");
  const buildings = formData.getAll("buildings");

  // בניית messageDetails
  let messageDetails = {};
  if (data.messageType === "יום הולדת") {
    messageDetails = {
      celebratorName: data["birthdayDetails.celebratorName"],
      greeting: data["birthdayDetails.greeting"],
      birthdayDate: data["birthdayDetails.birthdayDate"],
    };
  } else {
    messageDetails = {
      messageOption: data["generalMessageDetails.messageOption"] || "קבועה",
      startingDate: data["generalMessageDetails.startingDate"] || "",
      expirationDate: data["generalMessageDetails.expirationDate"] || "",
      text: data["generalMessageDetails.text"] || "",
    };
  }

  const payload = {
    messageType: data.messageType,
    messageDetails,
    target: {
      type: data.targetType,
      cities,
      streets,
      buildings,
    },
  };

  try {
    await customFetch.put(`/messages/${params.id}`, payload);
    toast.success("ההודעה עודכנה בהצלחה");
    return redirect("/dashboard/show-messages");
  } catch (error) {
    toast.error(error.response?.data?.msg || "שגיאה בעדכון ההודעה");
    return null;
  }
}

const EditMessage = () => {
  const { user } = useOutletContext();
  const { message, buildings } = useLoaderData();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const { id } = useParams();

  // --- State init from loader ---
  const [targetType, setTargetType] = useState(message.target.type);
  const [selectedCities, setSelectedCities] = useState(message.target.cities);
  const [selectedStreets, setSelectedStreets] = useState(
    message.target.streets
  );
  const [selectedBuildings, setSelectedBuildings] = useState(
    message.target.buildings.map((b) => b.toString())
  );
  const [formData, setFormData] = useState({
    messageType: message.messageType,
    generalMessageDetails:
      message.messageType === "כללית"
        ? { 
            messageOption: message.messageDetails.messageOption || "קבועה",
            startingDate: message.messageDetails.startingDate || "",
            expirationDate: message.messageDetails.expirationDate || "",
            text: message.messageDetails.text || ""
          }
        : {
            messageOption: "קבועה",
            startingDate: "",
            expirationDate: "",
            text: "",
          },
    birthdayDetails:
      message.messageType === "יום הולדת"
        ? { 
            celebratorName: message.messageDetails.celebratorName || "",
            greeting: message.messageDetails.greeting || "",
            birthdayDate: message.messageDetails.birthdayDate || ""
          }
        : { celebratorName: "", greeting: "", birthdayDate: "" },
  });

  // --- Build available lists ---
  const [availableCities, setAvailableCities] = useState([]);
  const [availableStreets, setAvailableStreets] = useState([]);
  const [availableBuildings, setAvailableBuildings] = useState([]);

  // populate cities
  useEffect(() => {
    const uniq = [...new Set(buildings.map((b) => b.city))];
    setAvailableCities(uniq);
  }, [buildings]);

  // streets per selectedCities
  useEffect(() => {
    if (selectedCities.length) {
      const sts = [
        ...new Set(
          buildings
            .filter((b) => selectedCities.includes(b.city))
            .map((b) => b.street)
        ),
      ];
      setAvailableStreets(sts);
    } else setAvailableStreets([]);
  }, [selectedCities, buildings]);

  // buildings per selectedStreets
  useEffect(() => {
    if (selectedStreets.length) {
      setAvailableBuildings(
        buildings.filter((b) => selectedStreets.includes(b.street))
      );
    } else setAvailableBuildings([]);
  }, [selectedStreets, buildings]);

  // --- Handlers ---
  const handleTargetTypeChange = (e) => {
    const t = e.target.value;
    setTargetType(t);
    setSelectedCities([]);
    setSelectedStreets([]);
    setSelectedBuildings([]);
  };

  const toggleSelection = (val, list, setList) => {
    setList((prev) =>
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
    );
  };

  // Handle input changes for general message details
  const handleGeneralInputChange = (e) => {
    const { name, value } = e.target;
    const fieldName = name.replace('generalMessageDetails.', '');
    
    setFormData(prev => ({
      ...prev,
      generalMessageDetails: {
        ...prev.generalMessageDetails,
        [fieldName]: value
      }
    }));
  };

  // Handle input changes for birthday details
  const handleBirthdayInputChange = (e) => {
    const { name, value } = e.target;
    const fieldName = name.replace('birthdayDetails.', '');
    
    setFormData(prev => ({
      ...prev,
      birthdayDetails: {
        ...prev.birthdayDetails,
        [fieldName]: value
      }
    }));
  };

  const handleMessageTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      messageType: e.target.value
    }));
  };

  const canSubmit = () => {
    if (formData.messageType === "כללית") {
      return formData.generalMessageDetails.text !== "";
    }
    if (formData.messageType === "יום הולדת") {
      const d = formData.birthdayDetails;
      return d.celebratorName && d.greeting && d.birthdayDate;
    }
    return false;
  };

  if (navigation.state === "loading") return <Loading />;

  console.log("formData", formData);

  return (
    <Wrapper className="full-page">
      {isSubmitting && <Loading />}
      <Form method="post" className="form">
        <h4 className="form-title">עריכת הודעה</h4>

        {/* hidden fields to include selection in request */}
        {selectedCities.map((c, i) => (
          <input type="hidden" name="cities" value={c} key={`hc-${i}`} />
        ))}
        {selectedStreets.map((s, i) => (
          <input type="hidden" name="streets" value={s} key={`hs-${i}`} />
        ))}
        {selectedBuildings.map((b, i) => (
          <input type="hidden" name="buildings" value={b} key={`hb-${i}`} />
        ))}

        <div className="form-center">
          {/* target scope */}
          <FormRowSelectId
            labelText="היקף ההודעה"
            name="targetType"
            value={targetType}
            defaultValue={targetType}
            handleChange={handleTargetTypeChange}
            idField="id"
            displayField="name"
            list={[
              { id: "global", name: "כל הבניינים" },
              { id: "city", name: "ערים נבחרות" },
              { id: "street", name: "רחובות נבחרים" },
              {
                id: "building",
                name: "בניינים ספציפיים",
              },
            ]}
          />

          {targetType === "city" && (
            <ListSelection
              labelText="בחר ערים"
              items={availableCities}
              selectedItems={selectedCities}
              handleItemSelection={(c) =>
                toggleSelection(c, selectedCities, setSelectedCities)
              }
            />
          )}

          {targetType === "street" && (
            <>
              <ListSelection
                labelText="ערים"
                items={availableCities}
                selectedItems={selectedCities}
                handleItemSelection={(c) =>
                  toggleSelection(c, selectedCities, setSelectedCities)
                }
              />
              <ListSelection
                labelText="רחובות"
                items={availableStreets}
                selectedItems={selectedStreets}
                handleItemSelection={(s) =>
                  toggleSelection(s, selectedStreets, setSelectedStreets)
                }
              />
            </>
          )}

          {targetType === "building" && (
            <>
              <ListSelection
                labelText="ערים"
                items={availableCities}
                selectedItems={selectedCities}
                handleItemSelection={(c) =>
                  toggleSelection(c, selectedCities, setSelectedCities)
                }
              />
              <ListSelection
                labelText="רחובות"
                items={availableStreets}
                selectedItems={selectedStreets}
                handleItemSelection={(s) =>
                  toggleSelection(s, selectedStreets, setSelectedStreets)
                }
              />
              <ListSelection
                labelText="בניינים"
                items={availableBuildings.map((b) => ({
                  id: b._id.toString(),
                  label: `${b.street} ${b.houseNumber}`,
                }))}
                selectedItems={selectedBuildings}
                handleItemSelection={(b) =>
                  toggleSelection(b, selectedBuildings, setSelectedBuildings)
                }
              />
            </>
          )}

          {/* message type */}
          <FormRowSelect
            labelText="סוג ההודעה"
            name="messageType"
            list={["כללית", "יום הולדת"]}
            value={formData.messageType}
            defaultValue={formData.messageType}
            handleChange={handleMessageTypeChange}
          />

          {formData.messageType === "כללית" && (
            <GeneralMessageDetails
              formData={formData.generalMessageDetails}
              handleInputChange={handleGeneralInputChange}
            />
          )}

          {formData.messageType === "יום הולדת" && (
            <BirthdayMessageDetails
              formData={formData.birthdayDetails}
              handleInputChange={handleBirthdayInputChange}
            />
          )}

          <div className="btn-container">
            <button
              type="submit"
              className="btn btn-block form-btn"
              disabled={isSubmitting || !canSubmit()}
            >
              {isSubmitting ? "שומר..." : "עדכן הודעה"}
            </button>
          </div>
        </div>
      </Form>
    </Wrapper>
  );
};

export default EditMessage;